import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';

import { dialog, dom } from 'app/utils';
import { Button } from 'app/components/common';
import { Form } from 'app/components/layout';
import { TextArea } from 'app/components/form';
import { confirmations } from 'app/services';
import { IConfirmation } from 'app/models';

export interface IRejection {
  data: IConfirmation;

  onDone: (data?: IConfirmation) => void;
}

export default memo(({ data, onDone }: IRejection) => {
  const { t } = useTranslation();

  const controller = useFormik<Pick<IConfirmation, 'reason'>>({
    initialValues: { reason: '' },
    validationSchema: useMemo(() => yup.object({
      reason: yup.string().required(),
    }), []),

    validateOnBlur: true,
    validateOnChange: false,
    validateOnMount: true,

    onSubmit: () => { },
  }), { handleSubmit, handleReset } = controller;
  const [busy, setBusy] = useState(false);

  const onSave = () => {
    if (!controller.isValid) { dialog.toast({ message: t('app.validation.required'), severity: 'error' }); return; }

    setBusy(true);
    confirmations.reject({ ...data, ...controller.values }).then(({ success, data }) => {
      if (!success) return;

      dialog.toast({ severity: 'success', message: t('confirmations.reject.success') });
      onDone(data);
    }).finally(() => setBusy(false));
  };

  const { touched, errors } = controller;
  const getProps = useCallback((field: keyof Pick<IConfirmation, 'reason'>): any => ({ ...controller.getFieldProps(field),
    invalid: !!(touched[field] && errors[field]),
  }), [controller, touched, errors]);

  const form: any = {
    className: dom.className('block', 'flex-auto', 'overflow-auto'),
    style: { minWidth: '36rem' },

    onSubmit: handleSubmit,
    onReset: handleReset,
  };

  return (
    <>
      <form { ...form }>
        <Form responsive={true}>
          <TextArea { ...getProps('reason') } placeholder={t('confirmations.reject.reason')} className="w-full" style={{ height: '6rem' }} />
        </Form>
      </form>
      <div className="mt-4 flex justify-content-end">
        <Button text label={t('app.cancel')} disabled={busy} className="mr-2" onClick={() => onDone()} />
        <Button severity="danger" label={t('app.reject')} disabled={busy} onClick={onSave} />
      </div>
    </>
  );
});
